/**
 * Sessão do portal: token de acesso curto (30 min) + refresh longo (30 dias).
 *
 * Antes disto o portal simplesmente caía quando o JWT vencia — e como não havia refresh, "cair"
 * significava jogar a pessoa na tela de login no meio do que ela estava fazendo. Aconteceu três
 * vezes durante o próprio trabalho que criou este arquivo.
 *
 * O backend (`POST /Auth/Refresh`) **rotaciona** o refresh a cada uso: o apresentado é marcado como
 * usado e um novo é emitido. Isso torna a serialização obrigatória — ver `renew()`.
 */
const ACCESS = "jwtToken";
const REFRESH = "refreshToken";

export const getAccessToken = () => localStorage.getItem(ACCESS);
export const getRefreshToken = () => localStorage.getItem(REFRESH);

export function saveSession(accessToken: string, refreshToken?: string | null) {
  localStorage.setItem(ACCESS, accessToken);
  // Backend anterior à change de sessão não devolve refresh; guardar `undefined` viraria a string
  // "undefined" e o portal tentaria renovar com lixo.
  if (refreshToken) localStorage.setItem(REFRESH, refreshToken);
}

export function clearSession() {
  localStorage.removeItem(ACCESS);
  localStorage.removeItem(REFRESH);
}

/**
 * Revoga o refresh no servidor ao sair (`POST /Auth/Logout`, sempre 204). O token é lido de forma
 * síncrona, então pode-se chamar sem `await` e limpar o storage em seguida.
 *
 * Melhor esforço: falha de rede não pode impedir o logout local — o pior caso é o refresh ficar
 * válido no servidor até expirar, o que era o comportamento antes desta chamada existir.
 */
export function revogarRefresh(post: (body: unknown) => Promise<any>): Promise<void> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve();
  return post({ refreshToken }).then(
    () => undefined,
    (erro) => {
      console.warn("Não foi possível revogar a sessão no servidor; o logout local segue.", erro);
    }
  );
}

/**
 * Renovação em curso. É o mesmo cuidado do widget, e pelo mesmo motivo: o portal dispara várias
 * chamadas em paralelo (uma tela abre 3–4 endpoints juntos). Se cada 401 renovasse por conta
 * própria, a segunda apresentaria um refresh que o servidor já rotacionou — e a sessão cairia
 * exatamente no código escrito para não derrubá-la.
 */
let renewing: Promise<string | null> | null = null;

export function renew(post: (body: unknown) => Promise<any>): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  if (!renewing) {
    renewing = post({ refreshToken })
      .then((res) => {
        const novo = res?.data?.jwtToken ?? null;
        if (!novo) return null;
        saveSession(novo, res?.data?.refreshToken);
        return novo as string;
      })
      .catch((erro) => {
        // Só 400/401 dizem que o refresh é inválido (vencido, usado, revogado): aí a sessão acabou.
        // 429, 5xx e falha de rede NÃO consumiram o refresh — o servidor nem chegou a rotacioná-lo.
        // Limpar a sessão aqui derrubaria para o login quem só tropeçou num limitador ou numa
        // oscilação; devolver null faz só esta requisição falhar, e a próxima tenta renovar de novo.
        const status = (erro as { response?: { status?: number } } | null)?.response?.status;
        if (status === 400 || status === 401) clearSession();
        return null;
      })
      .finally(() => {
        renewing = null;
      });
  }
  return renewing;
}

const AVISO = "signInNotice";

/**
 * Guarda a frase do servidor para a tela de login mostrar. O redirecionamento do interceptor é um
 * carregamento completo da página (ele vive fora do router), então o aviso atravessa por
 * sessionStorage e não por `location.state`.
 */
export function guardarAvisoDeLogin(mensagem: string) {
  sessionStorage.setItem(AVISO, mensagem);
}

/** Lê e apaga: o aviso vale para UMA exibição, não reaparece num refresh da tela de login. */
export function consumirAvisoDeLogin(): string {
  const m = sessionStorage.getItem(AVISO) ?? "";
  sessionStorage.removeItem(AVISO);
  return m;
}
